import React, { useCallback, useEffect, useState } from 'react';
import EditorWrapper from '../../../editor/EditorWrapper';
import { TiptapMenuType } from '../../../editor/utils';
import {
  convertStringToTiptapOutput,
  getTextWithEntityPlaceholders,
  TiptapOutputType,
} from '@amzn/awscat-editor';
import constants from '../../../common/constants';
import { FormField } from '@amzn/awsui-components-core';
import InfoLink from '../../common/InfoLink';

interface Props {
  onInputUpdated: (sectionId: string, value: any, validationError?: string) => void;
  onValidationError?: (validationError?: string) => void;
  sectionId: string;
  value: string;
  label: string;
  description?: string;
  contextualHelp?: string;
  isRequired?: boolean;
  stretch?: boolean;
  showValidationError?: boolean;
  disabled?: boolean;
  minLength: number;
  maxLength: number;
  removeFormatting?: boolean;
  singleLine?: boolean;
  hideMenuBar?: boolean;
  hideFooter?: boolean;
}

const MetadataEditorInput: React.FunctionComponent<Props> = (props: Props) => {
  const {
    onInputUpdated,
    value,
    label,
    description,
    contextualHelp,
    isRequired,
    showValidationError,
    disabled,
    maxLength,
    minLength,
    onValidationError,
    sectionId,
    stretch,
    removeFormatting,
    singleLine,
    hideMenuBar,
    hideFooter,
  } = props;

  const validateInput = useCallback(
    (input: any) => {
      let error: string = '';
      const inputString = getTextWithEntityPlaceholders(input);
      if (inputString && (inputString.length > maxLength || inputString.length < minLength)) {
        error = `Must be between ${minLength} and ${maxLength} characters.`;
      }
      if (isRequired && !inputString) {
        error = constants.REQUIRED;
      }
      if (onValidationError) {
        onValidationError(error);
      }
      setValidationError(error);
      return error;
    },
    [maxLength, minLength, isRequired, onValidationError],
  );

  const [validationError, setValidationError] = useState<string>('');
  const [inputValue, setInputValue] = useState<string>(value);

  useEffect(() => {
    validateInput(inputValue);
  }, [inputValue]); // eslint-disable-line react-hooks/exhaustive-deps

  useEffect(() => {
    setInputValue(value);
    validateInput(value);
  }, [value]); // eslint-disable-line react-hooks/exhaustive-deps

  useEffect(() => {
    if (validationError && !showValidationError) {
      setValidationError('');
    }
  }, [showValidationError, validationError]);

  const handleInput = (input: TiptapOutputType) => {
    const error = validateInput(input);
    const inputString = getTextWithEntityPlaceholders(input);

    setValidationError(error);
    setInputValue(inputString);

    if (sectionId && onInputUpdated) {
      onInputUpdated(sectionId, inputString, error);
    }
  };

  return (
    <FormField
      errorText={showValidationError ? validationError : ''}
      label={
        <span>
          {label}
          {!isRequired && <i> - optional</i>}
        </span>
      }
      info={contextualHelp && <InfoLink topic={contextualHelp} />}
      description={description}
      stretch={stretch}
      i18nStrings={{ errorIconAriaLabel: 'Error' }}
    >
      <EditorWrapper
        model={convertStringToTiptapOutput(inputValue)}
        readOnly={false}
        onModelChange={handleInput}
        menuType={TiptapMenuType.Hide}
        disabled={disabled}
        removeFormatting={!!removeFormatting}
        singleLine={!!singleLine}
        hideMenuBar={hideMenuBar}
        hideFooter={hideFooter}
      />
    </FormField>
  );
};

export default MetadataEditorInput;
